'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import {
  Badge as MokoBadge,
  Button,
  Skeleton,
  toast,
} from '@mokoboko/components';
import { formatCycles, formatPrice, mergeClassNames } from '@/lib/utils';
import {
  isSinglePayment,
  planClassesLabel,
  planClassesTerm,
  subscriptionCardFlagsOf,
} from '@/helpers/subscription';
import { AppearanceSettings } from '@/state/appearance-settings';
import { Subscription } from '@/state/subscription-groups';
import { ChevronDown } from '@/icons';
import { trackMetaConversion } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { CartData } from '@/lib/types';
import { goToCheckout } from '@/helpers/checkout-navigation';
import { useDetailHref } from '@/contexts/embed';
import { cardImageOf } from '@/lib/entity-image';
import {
  CARD_SIDE_BOX_CLASS,
  CARD_SIDE_BOX_LINES_CLASS,
  CARD_SIDE_BOX_TITLE_CLASS,
  CARD_TITLE_LINK_CLASS,
} from '@/lib/prose';
import { membershipGuestPassLine } from '@/helpers/guest-pass';
import { DotSeparated } from './dot-separated';
import { FocalPointImage } from './focal-point-image';

type SubscriptionCardProps = {
  subscription: Subscription;
  subscriptionSettings: AppearanceSettings['subscription'];
};

export const SubscriptionCard = ({
  subscription,
  subscriptionSettings,
}: SubscriptionCardProps) => {
  const router = useRouter();
  const detailHref = useDetailHref(
    'subscription',
    subscription.subscription_id,
    subscription.slug
  );

  const isPopular = subscription.is_popular;
  const flags = subscriptionCardFlagsOf(subscriptionSettings);
  const { url: cardImageUrl, focalX, focalY } = cardImageOf(subscription);
  const showImage = !!cardImageUrl && flags.showImage;

  const handleCardClick = () => {
    router.push(detailHref);
  };

  const handleCheckout = () => {
    const cartData: CartData<'subscription'> = {
      items: [
        {
          ...subscription,
          quantity: 1,
          price: subscription.price ?? 0,
        },
      ],
      totalAmount: subscription.price ?? 0,
      checkoutType: 'subscription',
    };

    if (!goToCheckout(router, cartData)) {
      toast.info('Allow pop-ups to continue to checkout.');
      return;
    }

    trackMetaConversion({
      event: 'AddToCart',
      custom_data: {
        content_name: subscription.name,
        content_type: 'subscription',
        content_ids: [subscription.slug],
        value: subscription.price ?? 0,
        ...(subscription.currency && { currency: subscription.currency }),
      },
    });
    track({
      event: AnalyticsEvent.PRODUCT_ADDED_TO_CART,
      properties: {
        product_type: 'subscription',
        product_name: subscription.name,
        product_id: subscription.subscription_id,
        price: subscription.price ?? null,
        currency: subscription.currency ?? null,
        quantity: 1,
      },
    });
  };

  const classesPerCycle = subscription.total_number_of_classes_per_cycle;
  const classesLabel = planClassesLabel(classesPerCycle);
  const singlePayment = isSinglePayment(subscription);
  const showClasses = classesPerCycle == null || classesPerCycle > 0;
  const intervalText = planClassesTerm(subscription, { shorten: false });
  const showCycles =
    !singlePayment &&
    !!subscription.number_of_billing_cycles &&
    subscription.number_of_billing_cycles > 0;
  const priceLabel =
    flags.showPrice && subscription.price != null
      ? formatPrice(subscription.price, subscription.currency)
      : null;
  const guestPassLine = membershipGuestPassLine(subscription);
  const cyclesLabel =
    showCycles && formatCycles(subscription.number_of_billing_cycles!);

  return (
    <div className="flex w-full flex-row gap-4 md:gap-5">
      <div className={mergeClassNames(CARD_SIDE_BOX_CLASS, 'md:w-[130px]')}>
        <div className={CARD_SIDE_BOX_LINES_CLASS}>
          {showClasses && (
            <p className={CARD_SIDE_BOX_TITLE_CLASS}>{classesLabel}</p>
          )}
          {subscription.charge_every_interval && (
            <p className="text-sm text-gray-700">{intervalText}</p>
          )}
        </div>
        {priceLabel && (
          <div className="md:hidden">
            <MokoBadge
              label={priceLabel}
              color="gray"
              size="md"
              className="bg-gray-200"
            />
          </div>
        )}
      </div>

      <div
        className="group flex w-full cursor-pointer overflow-hidden rounded-xl border border-gray-200 shadow-xs transition-shadow duration-200 max-md:flex-col md:hover:shadow-lg"
        onClick={handleCardClick}
      >
        {isPopular && (
          <MokoBadge
            label="Most popular"
            color="red"
            size="md"
            className="h-7 w-full justify-center rounded-b-none rounded-t-[11px] border-0 bg-popular-badge-fill px-4 py-1 text-popular-badge-ink md:hidden"
          />
        )}
        <div
          className={mergeClassNames(
            'flex w-full flex-col gap-5 p-4 md:gap-6 md:p-5',
            !flags.showDescription ||
              (!subscription?.short_description && !subscription?.description)
              ? 'md:gap-5'
              : ''
          )}
        >
          <div className="flex flex-col gap-2 md:hidden">
            <Link
              href={detailHref}
              className={mergeClassNames(
                CARD_TITLE_LINK_CLASS,
                'group-hover:text-red-500'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {subscription.name}
            </Link>
            <DotSeparated
              parts={[cyclesLabel, guestPassLine].filter(Boolean)}
            />
            {flags.showDescription && subscription?.short_description && (
              <p className="text-sm text-gray-700">
                {subscription.short_description}
              </p>
            )}
          </div>

          <div className="hidden w-full flex-row gap-5 md:flex">
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex items-center gap-2.5">
                <Link
                  href={detailHref}
                  className={mergeClassNames(
                    CARD_TITLE_LINK_CLASS,
                    'group-hover:text-red-500'
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  {subscription.name}
                </Link>
                {subscription.is_popular && (
                  <MokoBadge
                    label="Most popular"
                    color="red"
                    size="sm"
                    className="bg-popular-badge-fill text-popular-badge-ink"
                  />
                )}
              </div>
              <DotSeparated
                parts={[priceLabel, cyclesLabel, guestPassLine].filter(Boolean)}
              />
            </div>
            <div className="shrink-0">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCheckout();
                }}
                className="group-hover:bg-red-500"
              >
                Choose plan
              </Button>
            </div>
          </div>

          {flags.showDescription && subscription.short_description && (
            <p className="hidden text-sm text-gray-700 md:block">
              {subscription.short_description}
            </p>
          )}

          {showImage && (
            <div className="relative h-[200px] w-full overflow-hidden rounded-xl md:hidden">
              <FocalPointImage
                src={cardImageUrl!}
                focalPointX={focalX}
                focalPointY={focalY}
              />
            </div>
          )}

          <div className="w-full md:hidden">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleCheckout();
              }}
              fullWidth
              className="group-hover:bg-red-500"
            >
              Choose plan
            </Button>
          </div>
        </div>
        {showImage && (
          <div
            className={mergeClassNames(
              'max-md:pb-1/2 relative hidden h-[200px] w-auto shrink-0 rounded-l-xl max-md:w-full md:block md:h-[100px] md:w-[100px]',
              flags.showDescription &&
                (subscription?.short_description || subscription?.description)
                ? 'h-full w-[156px] md:h-full md:w-[156px]'
                : ''
            )}
          >
            <FocalPointImage
              src={cardImageUrl!}
              focalPointX={focalX}
              focalPointY={focalY}
              className="absolute"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const SubscriptionCardSkeleton = () => {
  return (
    <div className="flex w-full flex-row gap-4 md:gap-5">
      <div className={mergeClassNames(CARD_SIDE_BOX_CLASS, 'md:w-[130px]')}>
        <div className={CARD_SIDE_BOX_LINES_CLASS}>
          <span className="md:hidden">
            <Skeleton text="body-sm" width={72} />
          </span>
          <span className="hidden md:block">
            <Skeleton text="body-lg" width={72} />
          </span>
          <Skeleton text="body-sm" width={80} />
        </div>
        <div className="md:hidden">
          <Skeleton height={24} width={60} />
        </div>
      </div>

      <div className="flex w-full overflow-hidden rounded-xl border border-gray-200 shadow-xs max-md:flex-col">
        <div className="flex w-full flex-col gap-5 p-4 md:gap-6 md:p-5">
          <div className="flex w-full flex-col gap-2 md:hidden">
            <Skeleton text="body-lg" width={176} />
            <Skeleton text="body-sm" width={112} />
            <Skeleton text="body-sm" className="w-3/4" />
          </div>

          <div className="hidden w-full flex-row gap-5 md:flex">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton text="body-lg" width={176} />
              <Skeleton text="body-sm" width={152} />
            </div>
            <Skeleton isRounded height={40} width={120} className="shrink-0" />
          </div>

          <div className="hidden w-full md:block">
            <Skeleton text="body-sm" className="w-3/4" />
          </div>

          <Skeleton isRounded height={200} width="100%" className="md:hidden" />

          <Skeleton isRounded height={40} width="100%" className="md:hidden" />
        </div>

        <Skeleton
          height="100%"
          width={156}
          className="hidden shrink-0 self-stretch !rounded-none md:block"
        />
      </div>
    </div>
  );
};
